
import * as React from "react"

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

export type DeviceType = 'mobile' | 'tablet' | 'desktop'

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    
    // Add event listener using the more modern approach
    mql.addEventListener("change", onChange)
    
    // Set initial value
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

export function useDeviceType(): DeviceType {
  const [deviceType, setDeviceType] = React.useState<DeviceType>('desktop')

  React.useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth
      if (width < MOBILE_BREAKPOINT) {
        setDeviceType('mobile')
      } else if (width < TABLET_BREAKPOINT) {
        setDeviceType('tablet')
      } else {
        setDeviceType('desktop')
      }
    }

    // Check on initial render
    checkDeviceType()

    // Listen for resize events
    window.addEventListener('resize', checkDeviceType)

    return () => {
      window.removeEventListener('resize', checkDeviceType)
    }
  }, [])

  return deviceType
}

// Hook to detect if the device has touch capabilities
export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = React.useState(false)

  React.useEffect(() => {
    const checkTouch = () => {
      setIsTouch(
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        ((navigator as unknown as { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
      )
    }
    
    checkTouch()
  }, [])

  return isTouch
}

// Hook to detect slow connections
export function useConnectionQuality() {
  const [connectionType, setConnectionType] = React.useState<string>('unknown')
  const [isSlowConnection, setIsSlowConnection] = React.useState<boolean>(false)

  React.useEffect(() => {
    // Check if the Network Information API is supported
    const connection = (navigator as unknown as {
      connection?: {
        effectiveType?: string
        downlink?: number
        saveData?: boolean
        addEventListener: (t: string, cb: () => void) => void
        removeEventListener: (t: string, cb: () => void) => void
      }
    }).connection
    
    if (connection) {
      const updateConnectionInfo = () => {
        setConnectionType(connection.effectiveType || 'unknown')
        setIsSlowConnection(
          connection.effectiveType === 'slow-2g' || 
          connection.effectiveType === '2g' ||
          connection.saveData === true
        )
      }
      
      updateConnectionInfo()
      connection.addEventListener('change', updateConnectionInfo)
      
      return () => {
        connection.removeEventListener('change', updateConnectionInfo)
      }
    }
  }, [])

  return { connectionType, isSlowConnection }
}
